import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
  OnDestroy,
  OnChanges,
  Input,
  Output,
  EventEmitter,
  SimpleChanges,
  inject,
  Type
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { ReactiveFormsModule, FormsModule, FormBuilder } from '@angular/forms';
import { Subject, takeUntil, Observable } from 'rxjs';
import { HttpResponse } from '@angular/common/http';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { LoadingSpinnerService } from '@k12/app-services';
import { SortDirection } from '@angular/material/sort';
import { CfiMatTableComponent, ColumnDefinition, ExpandableRow } from '@k12/tables';
import {
  School,
  SchoolRosterFilter,
  SchoolTermGradeDto,
  SchoolGradeYearDto,
  SchoolRosterStudent,
  StudentRosterResponse,
  IssueRestrictedFields,
  AttendanceType,
  RosterImportResult
} from '@k12/models';
import { SchoolsService } from '@k12/school-services';
import {
  DEFAULT_ROSTER_COLUMN_DEFINITIONS,
  DEFAULT_ROSTER_DISPLAYED_COLUMNS,
  DEFAULT_ROSTER_COLUMN_TO_API_FIELD_MAP
} from '../roster-column-definitions';
import { RosterImportDialogComponent } from './roster-import-dialog/roster-import-dialog.component';
import { STUDENT_RECORD_ROUTE } from '../student-record-route.token';

@Component({
  selector: 'lib-generic-roster',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './generic-roster.component.html',
  styleUrl: './generic-roster.component.scss',
  standalone: true,
  imports: [
    CfiMatTableComponent,
    CommonModule,
    MatButtonModule,
    MatDialogModule,
    MatIconModule,
    MatMenuModule,
    MatTooltipModule,
    ReactiveFormsModule,
    FormsModule,
    RouterLink
  ]
})
export class GenericRosterComponent implements OnInit, OnDestroy, OnChanges {
  @Input() currentSchool: School | null = null;
  @Input() editable = false;
  @Input() showAdminActions = false;
  @Input() disableBackwardMove = false;
  @Input() studentCardComponent: Type<unknown>;
  @Input() loadStudentsQuery!: (filters: SchoolRosterFilter) => Observable<StudentRosterResponse>;
  @Input() columnDefinitions: ColumnDefinition[] = DEFAULT_ROSTER_COLUMN_DEFINITIONS;
  @Input() displayedColumns: string[] = DEFAULT_ROSTER_DISPLAYED_COLUMNS;
  @Input() columnToApiFieldMap: { [key: string]: string } = DEFAULT_ROSTER_COLUMN_TO_API_FIELD_MAP;
  @Input() schoolTermId: string | null = null;
  @Input() academicYearId: number | null = null;
  @Input() reloadTrigger: number = 0;
  @Input() schoolTermGrades: SchoolTermGradeDto[] = [];
  @Input() schoolGradeYears: SchoolGradeYearDto[] = [];
  @Input() attendanceTypes: AttendanceType[] = [];
  @Input() exportQuery: ((mini: boolean, schoolTermId?: string | null, academicYearId?: number | null) => Observable<HttpResponse<Blob>>) | null = null;
  @Input() importQuery: ((file: File, schoolTermId?: string | null) => Observable<RosterImportResult>) | null = null;
  @Input() rosterTitle: string = '';
  @Input() emptyStateMessage: string = '';
  /** When true, the roster is gated (e.g. before the certification window) — hides import/export. */
  @Input() accessGated = false;
  @Input() termName: string | null = null;
  @Input() searchTerm: string = '';
  @Input() issueRestrictions: IssueRestrictedFields | null = null;
  /** Query params appended to the student record link; kept as a stable reference for the binding. */
  protected studentRecordQueryParams: { academicYearId?: number } = {};

  private readonly studentRecordRoute = inject(STUDENT_RECORD_ROUTE, { optional: true });

  /**
   * Base route for the Student ID link, or null to render plain text. Supplied by the host app via
   * STUDENT_RECORD_ROUTE — see that token for why this is not an @Input.
   */
  protected get studentRecordBasePath(): string | null {
    return this.studentRecordRoute?.() ?? null;
  }
  @Output() countChanged = new EventEmitter<number>();
  @Output() certified = new EventEmitter<string>();
  @Output() endorsed = new EventEmitter<string>();
  @Output() saved = new EventEmitter<string>();
  @Output() reverted = new EventEmitter<string>();
  @Output() withdrawalRequested = new EventEmitter<SchoolRosterStudent>();

  private readonly destroy$ = new Subject<void>();
  private readonly fb = inject(FormBuilder);
  private readonly schoolsService = inject(SchoolsService);
  private readonly loadingSpinnerService = inject(LoadingSpinnerService);
  private readonly matDialog = inject(MatDialog);
  private readonly cdr = inject(ChangeDetectorRef);

  students: (SchoolRosterStudent & ExpandableRow)[] = [];
  expandedStudent: (SchoolRosterStudent & ExpandableRow) | null = null;
  isLoading = true;
  currentPageIndex = 0;
  currentPageSize = 100;
  totalCount = 0;

  currentSortColumn: string = 'studentDisplayId';
  currentSortDirection: SortDirection = 'asc';

  constructor() {}

  ngOnInit(): void {
    this.loadStudents();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['academicYearId']) {
      // Rebuilt only when the year changes so the template binds a stable reference.
      this.studentRecordQueryParams = this.academicYearId ? { academicYearId: this.academicYearId } : {};
    }
    if (changes['currentSchool'] && !changes['currentSchool'].firstChange && this.currentSchool) {
      this.currentPageIndex = 0;
      this.students = [];
      this.loadStudents();
    }
    if (changes['schoolTermId'] && !changes['schoolTermId'].firstChange) {
      this.currentPageIndex = 0;
      this.students = [];
      this.loadStudents();
    }
    if (changes['academicYearId'] && !changes['academicYearId'].firstChange) {
      this.currentPageIndex = 0;
      this.students = [];
      this.loadStudents();
    }
    if (changes['reloadTrigger'] && !changes['reloadTrigger'].firstChange) {
      this.currentPageIndex = 0;
      this.students = [];
      this.loadStudents();
    }
    
    if (changes['accessGated']) {
      this.cdr.markForCheck();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadStudents(): void {
    if (!this.currentSchool || !this.schoolsService) {
      this.isLoading = false;
      this.loadingSpinnerService.hideSpinner();
      this.cdr.markForCheck();
      return;
    }
    this.isLoading = true;
    this.loadingSpinnerService.showSpinner();
    this.cdr.markForCheck();
    const filters: SchoolRosterFilter = {
      schoolId: this.currentSchool.programPartnerId!,
      schoolTermId: this.schoolTermId || undefined,
      pageNumber: this.currentPageIndex + 1,
      pageSize: this.currentPageSize,
      orderBy: this.getApiFieldName(this.currentSortColumn),
      orderDirection: this.currentSortDirection || 'asc',
      searchTerm: this.searchTerm || undefined,
      academicYearId: this.academicYearId || undefined
    };

    this.loadStudentsQuery(filters)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: StudentRosterResponse) => {
          const enrollments = response.entity.items || [];
          this.students = this.mapEnrollmentsToStudents(enrollments);
          this.totalCount = response.entity.totalCount || 0;
          this.countChanged.emit(this.totalCount);
          this.loadingSpinnerService.hideSpinner();
          this.isLoading = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.loadingSpinnerService.hideSpinner();
          this.isLoading = false;
          this.cdr.markForCheck();
        }
      });
  }

  private mapEnrollmentsToStudents(enrollments: SchoolRosterStudent[]): (SchoolRosterStudent & ExpandableRow)[] {
    return enrollments.map((enrollment) => {
      const mappedStudent = {
        ...enrollment,
        id: enrollment.studentEnrollmentId,
        expanded: false
      };
      return {
        ...mappedStudent,
        subCells: [mappedStudent]
      };
    });
  }

  private getApiFieldName(columnName: string): string {
    return this.columnToApiFieldMap[columnName] || columnName;
  }

  onToggleExpand(studentEnrollmentId: string): void {
    const student = this.students.find((s) => s.studentEnrollmentId === studentEnrollmentId);
    if (student) {
      const wasExpanded = student['isExpanded'];
      // Close all other students
      this.students.forEach((s) => {
        if (s.studentEnrollmentId !== studentEnrollmentId) {
          s['isExpanded'] = false;
        }
      });
      // Toggle the clicked student
      student['isExpanded'] = !wasExpanded;
      this.expandedStudent = student['isExpanded'] ? student : null;
      this.cdr.markForCheck();
    }
  }

  onSort(sortEvent: { active: string; direction: SortDirection }): void {
    this.currentSortColumn = sortEvent.active;
    this.currentSortDirection = sortEvent.direction || 'asc';
    this.currentPageIndex = 0;
    this.loadStudents();
  }

  onPageChange(event: { pageIndex: number; pageSize: number }): void {
    this.currentPageIndex = event.pageIndex;
    this.currentPageSize = event.pageSize;
    this.loadStudents();
  }

  onStudentUpdate(): void {
    this.loadStudents();
  }

  onStudentActionCompleted(studentEnrollmentId: string): void {
    // Remove student from local array immediately for better UX
    this.students = this.students.filter((s) => s.studentEnrollmentId !== studentEnrollmentId);

    // Decrement count and notify parent
    this.totalCount = Math.max(0, this.totalCount - 1);
    this.countChanged.emit(this.totalCount);

    // Clear expanded student if it was the one removed
    if (this.expandedStudent?.studentEnrollmentId === studentEnrollmentId) {
      this.expandedStudent = null;
    }
    this.cdr.markForCheck();
  }

  onStudentCountIncrement(): void {
    // Increment count and notify parent
    this.totalCount = this.totalCount + 1;
    this.countChanged.emit(this.totalCount);
    this.cdr.markForCheck();
  }

  onStudentCertified(studentEnrollmentId: string): void {
    this.certified.emit(studentEnrollmentId);
  }

  onStudentEndorsed(studentEnrollmentId: string): void {
    this.endorsed.emit(studentEnrollmentId);
  }

  onStudentSaved(studentEnrollmentId: string): void {
    // Reload students to get fresh data after save
    this.loadStudents();
    this.saved.emit(studentEnrollmentId);
  }

  onStudentReverted(studentEnrollmentId: string): void {
    // Remove student from local array (they've moved to a different roster)
    this.students = this.students.filter((s) => s.studentEnrollmentId !== studentEnrollmentId);

    // Decrement count and notify parent
    this.totalCount = Math.max(0, this.totalCount - 1);
    this.countChanged.emit(this.totalCount);

    // Clear expanded student if it was the one removed
    if (this.expandedStudent?.studentEnrollmentId === studentEnrollmentId) {
      this.expandedStudent = null;
    }

    this.reverted.emit(studentEnrollmentId);
    this.cdr.markForCheck();
  }

  onWithdrawal(student: SchoolRosterStudent) {
    this.withdrawalRequested.emit(student);
  }

  triggerExport(mini: boolean): void {
    if (!this.exportQuery) return;
    this.loadingSpinnerService.showSpinner();
    this.exportQuery(mini, this.schoolTermId, this.academicYearId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: HttpResponse<Blob>) => {
          this.loadingSpinnerService.hideSpinner();
          const blob = response.body;
          if (!blob) return;
          const now = new Date();
          const pad = (n: number) => String(n).padStart(2, '0');
          const timestamp = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())} ${pad(now.getHours())}-${pad(now.getMinutes())}-${pad(now.getSeconds())}`;
          const schoolName = this.currentSchool?.name ?? '';
          const nameParts = [schoolName, this.rosterTitle, this.termName].filter(Boolean);
          const fileName = `${nameParts.join(' - ')} - ${timestamp}.xlsx`;
          // Note: Backend Content-Disposition header is ignored to ensure term name is always included
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = fileName;
          a.style.display = 'none';
          document.body.appendChild(a);
          a.click();
          setTimeout(() => {
            URL.revokeObjectURL(url);
            a.remove();
          }, 100);
        },
        error: () => {
          this.loadingSpinnerService.hideSpinner();
        }
      });
  }

  openImportDialog(): void {
    if (!this.importQuery) return;
    const ref = this.matDialog.open(RosterImportDialogComponent, {
      width: '520px',
      disableClose: true,
      data: {
        importQuery: (file: File) => (this.importQuery ? this.importQuery(file, this.schoolTermId) : null)
      }
    });
    ref.afterClosed().subscribe((result: RosterImportResult | null | undefined) => {
      if (result && result.successCount > 0) {
        this.loadStudents();
      }
    });
  }

  getFormattedAwards(student: SchoolRosterStudent): string {
    if (!student.studentAwards || student.studentAwards.length === 0) {
      return '—';
    }
    return student.studentAwards.map((award) => award.programName).join(', ');
  }

  getParentDisplayName(student: SchoolRosterStudent): string {
    const firstName = student.parentFirstName?.trim() ?? '';
    const lastName = student.parentLastName?.trim() ?? '';

    if (firstName && lastName) {
      return `${lastName}, ${firstName}`;
    }

    return lastName || firstName || '—';
  }
}
